import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getSacredEntry,
  sacredPath,
  displayName,
  createEmptySacredEntry,
} from "@/lib/sacred-library/utils/registry";
import {
  fetchEditorEntry,
  saveEditorEntry,
  submitEditorEntry,
  assetUrl,
} from "@/lib/sacred-library/sacred-library-api";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { BeginnerForm } from "./BeginnerForm";
import { DeepJsonEditor } from "./DeepJsonEditor";
import { ImageUploadPanel } from "./ImageUploadPanel";
import { LinkRegistryEditor } from "./LinkRegistryEditor";
import { SacredTopicImage } from "@/components/sacred-library/SacredTopicImage";
import {
  BeginnerDeityView,
  BeginnerFestivalView,
  BeginnerVrataView,
  BeginnerStoryView,
  BeginnerSadhanaView,
} from "@/components/sacred-library/BeginnerDeepView";

const DRAFT_KEY = (type, id) => `sacred_draft_${type}_${id}`;

export function SacredEditor({ topicType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditLibrary, canPublishLibrary, isAdmin, editorUser } = useSacredAdmin();
  const staticEntry = getSacredEntry(topicType, id);
  const base = staticEntry ?? createEmptySacredEntry(topicType, id);
  const isNewTopic = !staticEntry;

  const [entry, setEntry] = useState(null);
  const [links, setLinks] = useState({ id, type: topicType });
  const [jsonError, setJsonError] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const remote = await fetchEditorEntry(topicType, id);
      const merged = {
        ...base,
        ...(remote.published || {}),
        ...(remote.draft || {}),
        beginner: { ...base.beginner, ...(remote.published?.beginner || remote.draft?.beginner || {}) },
        deep: { ...base.deep, ...(remote.published?.deep || remote.draft?.deep || {}) },
        image_url: remote.image_url ? assetUrl(topicType, id) : base.image_url,
      };
      setEntry(merged);
      if (remote.links) setLinks(remote.links);
      else setLinks({ id, type: topicType, deity_ids: base.deity_ids || [] });
    } catch {
      const draftRaw = localStorage.getItem(DRAFT_KEY(topicType, id));
      if (draftRaw) {
        try {
          setEntry(JSON.parse(draftRaw));
        } catch {
          setEntry({ ...base });
        }
      } else {
        setEntry({ ...base });
      }
    }
    setLoading(false);
  }, [base, topicType, id, staticEntry]);

  useEffect(() => {
    if (!canEditLibrary) {
      navigate(sacredPath(topicType, id));
      return;
    }
    load();
  }, [canEditLibrary, load, navigate, topicType, id]);

  useEffect(() => {
    if (!entry) return;
    const t = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY(topicType, id), JSON.stringify(entry));
    }, 800);
    return () => clearTimeout(t);
  }, [entry, topicType, id]);

  if (!canEditLibrary) return null;
  if (loading || !entry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const title = displayName(topicType, entry);
  const needsApproval = editorUser?.approval_required && !canPublishLibrary;

  const buildPayload = () => ({
    entry: {
      ...entry,
      id,
      name: entry.name,
      title: entry.title,
    },
    links: { ...links, id, type: topicType },
  });

  const onSaveDraft = async () => {
    setSaving(true);
    setStatus("");
    try {
      await saveEditorEntry(topicType, id, { ...buildPayload(), draft_only: true });
      setStatus("Draft saved to server.");
    } catch (e) {
      setStatus("Draft saved locally only.");
    }
    setSaving(false);
  };

  const onPublish = async () => {
    setSaving(true);
    try {
      await saveEditorEntry(topicType, id, { ...buildPayload(), publish: true });
      setStatus("Published.");
      setTimeout(() => navigate(sacredPath(topicType, id)), 600);
    } catch (e) {
      setStatus(e.response?.data?.detail || "Publish failed");
    }
    setSaving(false);
  };

  const onSubmit = async () => {
    setSaving(true);
    try {
      await submitEditorEntry(topicType, id, buildPayload());
      setStatus("Submitted for admin approval.");
    } catch (e) {
      setStatus("Submit failed");
    }
    setSaving(false);
  };

  let preview = null;
  if (topicType === "deity") preview = <BeginnerDeityView entry={entry} />;
  else if (topicType === "festival") preview = <BeginnerFestivalView entry={entry} />;
  else if (topicType === "vrata") preview = <BeginnerVrataView entry={entry} />;
  else if (topicType === "story") preview = <BeginnerStoryView entry={entry} />;
  else if (topicType === "sadhana") preview = <BeginnerSadhanaView entry={entry} />;

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="sacred-editor">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" size="sm" className="mb-4" asChild>
          <Link to={sacredPath(topicType, id)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to topic
          </Link>
        </Button>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>
              {isNewTopic ? "New topic" : "Edit"}: {title}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isNewTopic && "Not in bundled catalog yet — publish to make it live. "}
              Beginner fields (left) + advanced deep JSON (right): tattva, mantras, pūjā, temples, etc.
              {needsApproval ? " Changes require admin approval." : " You can publish directly."}
            </p>
            {status && <p className="text-sm text-primary">{status}</p>}
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Beginner layer</CardTitle>
            </CardHeader>
            <CardContent>
              <BeginnerForm
                topicType={topicType}
                beginner={entry.beginner}
                name={entry.name}
                title={entry.title}
                onNameChange={(v) =>
                  setEntry((e) =>
                    topicType === "story" ? { ...e, title: v } : { ...e, name: v }
                  )
                }
                onChange={(b) => setEntry((e) => ({ ...e, beginner: b }))}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Deep layer (JSON)</CardTitle>
            </CardHeader>
            <CardContent>
              <DeepJsonEditor
                topicType={topicType}
                value={entry.deep}
                onChange={(d) => setEntry((e) => ({ ...e, deep: d }))}
                error={jsonError}
                onError={setJsonError}
              />
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <ImageUploadPanel
            topicType={topicType}
            id={id}
            imageUrl={entry.image_url}
            onUploaded={(url) => setEntry((e) => ({ ...e, image_url: assetUrl(topicType, id) }))}
          />
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Link registry</CardTitle>
            </CardHeader>
            <CardContent>
              <LinkRegistryEditor links={links} onChange={setLinks} />
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Live preview</CardTitle>
          </CardHeader>
          <CardContent>
            <SacredTopicImage
              type={topicType}
              src={entry.image_url}
              alt={title}
              size="detail"
              className="mx-auto mb-4 max-w-[320px] w-full"
            />
            {preview}
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 sticky bottom-4 bg-background/90 p-4 rounded-lg border shadow-lg">
          <Button type="button" variant="outline" onClick={onSaveDraft} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> Save draft
          </Button>
          {needsApproval ? (
            <Button type="button" onClick={onSubmit} disabled={saving || !!jsonError}>
              <Send className="w-4 h-4 mr-2" /> Submit for approval
            </Button>
          ) : (
            <Button type="button" onClick={onPublish} disabled={saving || !!jsonError}>
              Publish
            </Button>
          )}
          {isAdmin && (
            <Button type="button" variant="secondary" onClick={onPublish} disabled={saving}>
              Admin publish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
